import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import type { RouterOutputs } from "@alm/api-contracts";
import { formatTimestampInTimezone } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { optionalOneOf, searchPatch } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZoneFor } from "@/lib/facility/zone";
import { LiftBlockDialog } from "./-components/lift-block-dialog";
import { useStaffNames } from "./-components/use-staff-names";
import { VisitorNav } from "./-components/visitor-nav";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/blocked")({
  validateSearch: (search: Record<string, unknown>): { status?: "all" } => ({
    status: optionalOneOf(["all"], search.status),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: BlockedVisitorsPage,
});

type Block = RouterOutputs["visitors"]["blocks"]["list"][number];
type Status = "active" | "all";

const personName = (block: Block) => `${block.person.firstName} ${block.person.lastName}`;

function stateOf(block: Block) {
  if (block.active) return { label: "Blocked", tone: "crit" as const };
  if (block.liftedAt) return { label: "Lifted", tone: "neutral" as const };
  return { label: "Ended", tone: "neutral" as const };
}

function columns(
  zoneFor: (facilityId?: string | null) => string,
  nameOf: (userId: string | null) => string | null,
): ColumnDef<Block>[] {
  return [
    {
      id: "person",
      header: "Visitor",
      accessorFn: personName,
      enableHiding: false,
      enableSorting: false,
      meta: { className: "min-w-48" },
      cell: ({ row: { original: block } }) => (
        <div className="min-w-0">
          <Link
            to="/visitors/people/$personId"
            params={{ personId: block.personId }}
            className="focus-ring rounded-sm font-medium hover:underline"
          >
            {personName(block)}
          </Link>
          {block.person.company ? (
            <p className="text-muted-foreground truncate text-xs">{block.person.company}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: "reason",
      header: "Reason",
      accessorFn: (block) => block.reason,
      enableSorting: false,
      meta: { clamp: "md", lines: 2 },
    },
    {
      id: "blocked",
      header: "Blocked",
      accessorFn: (block) => new Date(block.blockedAt).getTime(),
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: block } }) => (
        <div>
          <RelativeTime value={block.blockedAt} timeZone={zoneFor(block.facilityId)} />
          <p className="text-muted-foreground text-xs">
            {nameOf(block.blockedBy) ?? block.blockedByName ?? "Staff"}
          </p>
        </div>
      ),
    },
    {
      id: "ends",
      header: "Ends",
      accessorFn: (block) => (block.expiresAt ? new Date(block.expiresAt).getTime() : 0),
      enableSorting: false,
      meta: { nowrap: true, priority: 2 },
      cell: ({ row: { original: block } }) =>
        block.expiresAt ? (
          <span className="tabular-nums">
            {formatTimestampInTimezone(block.expiresAt, zoneFor(block.facilityId))}
          </span>
        ) : (
          "When lifted"
        ),
    },
    {
      id: "state",
      header: "Status",
      accessorFn: (block) => stateOf(block).label,
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: block } }) => {
        const state = stateOf(block);
        return <Badge variant={state.tone}>{state.label}</Badge>;
      },
    },
  ];
}

/** Everyone who may not come in, with why, who decided, and until when. Security staff only. */
function BlockedVisitorsPage() {
  const { facilityId } = useFacility();
  const pinned = facilityId === "all" ? null : facilityId;
  const canSecure = useCan("visitor:security", pinned);
  const zoneFor = useZoneFor();
  const nameOf = useStaffNames(pinned);
  const { status: statusParam } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const status: Status = statusParam ?? "active";
  const setStatus = (next: Status) => patch({ status: next === "all" ? next : undefined });
  const [lifting, setLifting] = useState<Block | null>(null);
  const blocks = useQuery({
    ...orpc.visitors.blocks.list.queryOptions({ input: { facilityId, status } }),
    enabled: canSecure,
  });
  const rows = blocks.data ?? [];

  if (!canSecure) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Blocked visitors" />
        <VisitorNav />
        <Card>
          <EmptyState
            title="Blocked visitors are for security staff"
            description="Ask an administrator or the community manager if you need to see who is blocked."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Blocked visitors"
        count={blocks.isSuccess ? rows.length : undefined}
        meta="The desk, the kiosk and Grace refuse these people. The visitor is never told why."
      />
      <VisitorNav />
      {blocks.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the blocked visitors."
            error={blocks.error}
            onRetry={() => void blocks.refetch()}
            retrying={blocks.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={columns(zoneFor, nameOf)}
          noun="blocks"
          rowId={(block) => block.id}
          pending={blocks.isPending}
          renderToolbar={() => (
            <ListToolbar
              chips={
                <fieldset className="flex min-w-0 flex-wrap gap-1.5">
                  <legend className="sr-only">Filter by status</legend>
                  <FilterChip selected={status === "active"} onClick={() => setStatus("active")}>
                    In force
                  </FilterChip>
                  <FilterChip selected={status === "all"} onClick={() => setStatus("all")}>
                    All, with lifted and ended
                  </FilterChip>
                </fieldset>
              }
            />
          )}
          renderRowActions={(block) => (
            <RowActions
              label={`the block on ${personName(block)}`}
              modal={false}
              primary={
                block.active && {
                  label: "Lift block",
                  onSelect: () => setLifting(block),
                }
              }
              items={[
                {
                  label: "Open profile",
                  link: { to: "/visitors/people/$personId", params: { personId: block.personId } },
                },
              ]}
            />
          )}
          empty={
            <EmptyState
              title={status === "active" ? "Nobody is blocked" : "Nobody has been blocked"}
              description="Block a visitor from their profile."
              action={
                <Button asChild variant="outline">
                  <Link to="/visitors/people">Find a visitor</Link>
                </Button>
              }
            />
          }
        />
      )}
      {lifting ? (
        <LiftBlockDialog
          blockId={lifting.id}
          personName={personName(lifting)}
          onClose={() => setLifting(null)}
        />
      ) : null}
    </div>
  );
}
